<template>
  <div class="mcp-app-container">
    <!-- A tool this visualization wants to run needs the user's say-so. Shown
         above the frame rather than over it: the chart the question is about is
         usually the context for answering it.

         Deliberately a sibling of the frame, not a child: the frame is sized to
         the height the guest asked for and clips to it, so a prompt inside it
         was cut off to whatever the guest happened to be at -- often a few
         dozen pixels, because a visualization blocked on a tool call has not
         drawn anything yet. -->
    <McpToolPermissionPrompt
      v-if="pendingApproval"
      :record="pendingApproval"
      :submit="submitApproval"
    />
    <div class="mcp-app-frame" :style="frameStyle">
      <div v-if="loading" class="mcp-app-loading">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p>Loading...</p>
      </div>
      <div v-else-if="error" class="mcp-app-error">
        <i class="fa fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
      </div>
      <iframe
        v-show="!loading && !error"
        ref="iframe"
        :sandbox="sandboxAttrs"
        class="mcp-app-iframe"
        @load="onIframeLoad"
      />
    </div>
  </div>
</template>

<script>
import { AppBridge, PostMessageTransport } from '@modelcontextprotocol/ext-apps/app-bridge';
import { createAgentsApiHandlers, describeToolError } from '@/services/surfaceApiMcpAdapter';
import McpToolPermissionPrompt from '@/components/paice/McpToolPermissionPrompt.vue';

/**
 * Identity of a tool call: its name and the arguments it would run with.
 *
 * Key order is normalised because the two callers that can raise the same call
 * do not build the arguments the same way -- the host passes the object it was
 * given as a prop, the guest echoes back what it was sent over postMessage --
 * and `JSON.stringify` alone would make those two look different.
 */
function toolCallKey(name, args) {
  return `${name}::${stableStringify(args ?? {})}`;
}

function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  // Undefined-valued keys are dropped, as JSON.stringify drops them: the guest's
  // copy of the arguments reached it through a JSON round trip, so it no longer
  // has them, and the two sides have to key the same.
  const body = Object.keys(value)
    .filter((k) => value[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
    .join(',');
  return `{${body}}`;
}

export default {
  name: 'McpAppRenderer',

  components: { McpToolPermissionPrompt },

  props: {
    // URI of the UI resource to render
    resourceUri: {
      type: String,
      required: true
    },
    // Name of the tool associated with this UI (for direct tool calls)
    toolName: {
      type: String,
      default: null
    },
    // Server that tool came from (`serverId` on GET /aquabot/tools). Routes the
    // UI's resource reads and tool calls to it; external servers' UIs cannot be
    // loaded without it or the prefixed toolName.
    serverId: {
      type: String,
      default: null
    },
    // Tool input arguments to send to the guest UI
    toolInput: {
      type: Object,
      default: null
    },
    // Tool result to send to the guest UI
    toolResult: {
      type: Object,
      default: null
    },
    // Surface API base URL
    surfaceUrl: {
      type: String,
      required: true
    },
    // Authentication token
    accessToken: {
      type: String,
      required: true
    },
    // Client ID
    cid: {
      type: String,
      required: true
    },
    // Theme mode: 'dark' or 'light'
    theme: {
      type: String,
      default: 'light'
    },
    // Chat this app is rendered in, if any. Only used to file an approval with
    // the conversation it was asked from; approvals work without it.
    resourceId: {
      type: String,
      default: null
    },
  },

  data() {
    return {
      bridge: null,
      handlers: null, // Store handlers for direct tool calls
      loading: true,
      error: null,
      height: null,
      initialized: false,
      sandboxAttrs: 'allow-scripts allow-same-origin allow-forms',
      // The 202 payload for the tool call currently waiting on the user: the
      // head of `approvalQueue`, mirrored into reactive state so the template
      // can render it. Only one card shows at a time even when more than one
      // call is parked -- a second question stacked under the first would be
      // answered blind.
      pendingApproval: null
    };
  },

  created() {
    // Deliberately not in `data()`. A Promise put into reactive state comes
    // back out as a proxy, and awaiting the proxy throws, because a promise's
    // internals only exist on the raw object.
    //
    // Tool calls this card has already started, by name + arguments. Both the
    // startup call in `oninitialized` and the guest's own `tools/call` want the
    // same data, so whichever asks second is handed the first one's promise
    // rather than running the tool again -- one execution, and one approval
    // prompt, for what is really one call. Claimed once: an identical call
    // after that is a deliberate re-run and gets to run.
    this.pendingToolCalls = new Map();

    // Approvals waiting on the user, oldest first, as { record, resolve }.
    this.approvalQueue = [];
  },

  computed: {
    frameStyle() {
      return {
        height: this.height ? `${this.height}px` : 'auto'
      };
    }
  },

  watch: {
    // Send tool result when it becomes available
    toolResult: {
      handler(newResult) {
        if (newResult && this.bridge && this.initialized) {
          this.bridge.sendToolResult(this.toPlain(newResult));
        }
      },
      deep: true
    },

    // Update theme when it changes
    theme(newTheme) {
      if (this.bridge && this.initialized) {
        this.bridge.setHostContext({ theme: newTheme });
      }
    }
  },

  async mounted() {
    await this.initializeApp();
  },

  beforeUnmount() {
    this.cleanup();
  },

  methods: {
    // Strip Vue reactivity proxies and non-cloneable properties so postMessage works
    toPlain(obj) {
      return JSON.parse(JSON.stringify(obj));
    },

    async initializeApp() {
      try {
        this.loading = true;
        this.error = null;

        // Create Surface API handlers
        this.handlers = createAgentsApiHandlers(
          this.surfaceUrl,
          this.accessToken,
          this.cid,
          this.$store.state,
          {
            requestApproval: this.requestApproval,
            resourceId: this.resourceId,
            toolName: this.toolName,
            serverId: this.serverId
          }
        );

        // Fetch the HTML resource
        const resource = await this.handlers.readResource({ uri: this.resourceUri });
        let html = resource.contents[0].text;

        if (!html) {
          throw new Error('Empty resource content received');
        }

        // INJECT INITIAL THEME: 
        // Force the theme onto the document before the iframe even renders
        html = html.replace(
          '<head>', 
          "<head>\n   " + "<script>document.documentElement.dataset.theme = '" + this.theme + "';<" + "/script>"
        );

        // Create AppBridge with null client (we use Surface API handlers)
        this.bridge = new AppBridge(
          null,
          { name: 'CX-Portal', version: '1.0.0' },
          {
            openLinks: {},
            updateModelContext: { text: {} },
            message: { text: {} },
            logging: {}
          },
          {
            hostContext: {
              theme: this.theme,
              displayMode: 'inline',
              availableDisplayModes: ['inline']
            }
          }
        );

        // Register handlers for MCP operations
        this.bridge.onreadresource = async (params) => {
          return this.handlers.readResource(params);
        };

        // Handle size changes from guest UI
        this.bridge.onsizechange = ({ width, height }) => {
          if (height !== undefined) {
            this.height = height; // Remove Math.max() constraint
            this.$emit('size-changed', { width, height });
          }
        };

        // Handle messages from guest UI
        this.bridge.onmessage = async (params) => {
          this.$emit('message', params);
          return {};
        };

        // Handle model context updates
        this.bridge.onupdatemodelcontext = async (params) => {
          this.$emit('context-update', params);
          return {};
        };

        // Handle link opening
        this.bridge.onopenlink = async (params) => {
          window.open(params.url, '_blank', 'noopener,noreferrer');
          return {};
        };

        // Handle logging from guest UI
        this.bridge.onloggingmessage = (params) => {
          const level = params.level || 'info';
          const logger = params.logger || 'guest';
          console[level]?.(`[${logger}]`, params.data) || console.log(`[${logger}]`, params.data);
        };

        // Handle tool calls from guest UI (MCP Apps spec)
        this.bridge.oncalltool = async (params) => {
          console.log('Guest UI calling tool:', params.name, params.arguments);
          try {
            const result = await this.runTool(params.name, params.arguments);
            return this.toPlain(result);
          } catch (err) {
            console.error('Tool call failed:', err);
            // The service's own wording when it sent one — "this tool is turned
            // off in PAICE settings" is worth showing verbatim, where a bare
            // "Request failed with status code 403" is not.
            return {
              content: [{ type: 'text', text: describeToolError(err) }],
              isError: true
            };
          }
        };

        // Handle initialization completed
        this.bridge.oninitialized = async () => {
          console.log('MCP App initialized');
          console.log('  - toolInput:', this.toolInput);
          console.log('  - toolResult:', this.toolResult);
          console.log('  - toolName:', this.toolName);
          console.log('  - handlers:', !!this.handlers);
          this.initialized = true;
          this.loading = false;

          // Send tool input if available (default to empty object for tools with no args)
          const inputArgs = this.toolInput || {};
          console.log('Sending tool input:', inputArgs);
          this.bridge.sendToolInput(this.toPlain({ arguments: inputArgs }));

          // Send tool result if already available
          if (this.toolResult) {
            console.log('Sending existing tool result:', this.toolResult);
            this.bridge.sendToolResult(this.toPlain(this.toolResult));
          } else if (this.toolName && this.handlers) {
            // No tool result yet but we have tool name - call the tool directly
            console.log(`Calling tool "${this.toolName}" directly with input:`, inputArgs);
            try {
              const result = await this.runTool(this.toolName, inputArgs);
              console.log(`Tool "${this.toolName}" raw result:`, JSON.stringify(result, null, 2));

              // Format and send the result to the guest UI
              // Handle double-wrapped CallToolResult (where structuredContent contains the actual result)
              let actualContent = result?.content;
              let actualStructuredContent = result?.structuredContent;

              // Check if structuredContent itself contains content/structuredContent (double-wrapped)
              if (actualStructuredContent?.content && actualStructuredContent?.structuredContent) {
                console.log('Detected double-wrapped CallToolResult, unwrapping...');
                actualContent = actualStructuredContent.content;
                actualStructuredContent = actualStructuredContent.structuredContent;
              }

              const toolResult = {
                content: actualContent || [{ type: 'text', text: JSON.stringify(result) }],
                structuredContent: actualStructuredContent
              };
              console.log('Formatted tool result to send:', JSON.stringify(toolResult, null, 2));
              this.bridge.sendToolResult(this.toPlain(toolResult));
            } catch (err) {
              console.error(`Failed to call tool "${this.toolName}":`, err);
              this.bridge.sendToolResult({
                content: [{ type: 'text', text: describeToolError(err) }],
                isError: true
              });
            }
          } else {
            console.warn('Cannot call tool - missing toolName or handlers');
          }
        };

        // Connect transport BEFORE writing srcdoc so the host is already
        // listening when the guest app sends its initialize message.
        // (The guest's postMessage fires during script execution, which
        //  is dispatched before the iframe load event reaches the parent.)
        const iframe = this.$refs.iframe;
        const transport = new PostMessageTransport(
          iframe.contentWindow,
          iframe.contentWindow
        );
        await this.bridge.connect(transport);
        console.log('AppBridge transport connected, writing srcdoc');

        // Now load the guest content — the host is ready to receive initialize
        iframe.srcdoc = html;

      } catch (err) {
        console.error('Failed to initialize MCP App:', err);
        // 404: the server does not serve this view. The tool itself ran and its
        // result is in the chat, so say that rather than showing a failure.
        this.error = err?.response?.status === 404
          ? 'This tool\'s interactive view is not available. Its result is shown in the chat.'
          : describeToolError(err) || 'Failed to load visualization';
        this.loading = false;
      }
    },

    onIframeLoad() {
      console.log('Iframe load event fired');
    },

    /**
     * Run a tool for this card, or join the call already running for it.
     *
     * The host calls the tool itself on startup when it was handed no result,
     * and a guest handed tool input with no result fetches its own data — so
     * one visualization raises the same call twice, from both sides of the
     * bridge. Left alone that runs the tool twice and, on a tool set to `ask`,
     * asks the user twice for what they think is one thing.
     */
    runTool(name, args) {
      const key = toolCallKey(name, args);

      const started = this.pendingToolCalls.get(key);
      if (started) {
        // Claimed: this is the other caller taking the result of the call that
        // was already made. Anything identical after this is a re-run.
        this.pendingToolCalls.delete(key);
        return started;
      }

      const call = this.handlers.callTool({ name, arguments: args || {} });
      this.pendingToolCalls.set(key, call);
      return call;
    },

    /**
     * Park a tool call until the user answers, and resolve with their answer.
     *
     * Deliberately has no timeout. The tool call it belongs to is already
     * bounded by its own request timeout, and a card that vanished from under
     * someone mid-read would be worse than one that waits — the service expires
     * the approval on its own schedule either way.
     */
    requestApproval(prompt) {
      return new Promise((resolve) => {
        this.approvalQueue.push({ record: prompt, resolve });
        if (!this.pendingApproval) this.pendingApproval = prompt;
      });
    },

    /**
     * Write the answer, then release the waiting call.
     *
     * Order matters: the repeat request only works once the decision is on
     * record, so a failed write has to leave the card up and re-throw. The
     * prompt component catches that, shows it, and leaves the buttons live.
     */
    async submitApproval({ record, decision }) {
      await this.$store.dispatch('paice/sendMcpToolDecision', {
        resourceId: record.resourceId,
        toolUseId: record.toolUseId,
        decision,
        serverId: record.serverId,
        toolName: record.toolName
      });

      // Matched on toolUseId, which is what the decision was written against —
      // and the only identity that survives the trip through the card, since
      // `record` comes back as the reactive proxy of what the queue holds.
      let index = this.approvalQueue.findIndex(
        (entry) => entry.record.toolUseId === record.toolUseId
      );
      if (index === -1) index = 0;
      const [answered] = this.approvalQueue.splice(index, 1);

      this.pendingApproval = this.approvalQueue.length
        ? this.approvalQueue[0].record
        : null;
      if (answered) answered.resolve(decision);
    },

    async cleanup() {
      // Release everything parked on an approval, or the calls waiting on those
      // answers would never settle and their promises would leak with the
      // component.
      const parked = this.approvalQueue.splice(0);
      this.pendingApproval = null;
      parked.forEach(({ resolve }) => resolve('deny'));
      this.pendingToolCalls.clear();

      if (this.bridge) {
        try {
          // Attempt graceful teardown
          await this.bridge.teardownResource({});
        } catch (err) {
          // Ignore teardown errors
          console.warn('Teardown warning:', err);
        }

        try {
          await this.bridge.close();
        } catch (err) {
          // Ignore close errors
        }

        this.bridge = null;
      }
    }
  }
};
</script>

<style scoped>
.mcp-app-container {
  width: 100%;
}

/* Only the frame is sized to the guest's requested height; anything the host
   adds around it (the permission prompt) keeps its own height. */
.mcp-app-frame {
  width: 100%;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
}

.mcp-app-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.mcp-app-loading,
.mcp-app-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: #6c757d;
}

.mcp-app-loading i,
.mcp-app-error i {
  margin-bottom: 10px;
}

.mcp-app-error {
  color: #dc3545;
}

.mcp-app-error i {
  font-size: 2rem;
}
</style>
<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

body.dark-mode {
  .mcp-app-frame {
    background: $dark-card-bg;
  }
}
</style>
